/**
 * `loader` de las colas de Operaciones — ADR-0012, paso 2.
 *
 * El RUM señaló Ventas como la más lenta del panel. La idea: arrancar la consulta cuando arranca la
 * NAVEGACIÓN —antes de que el componente exista— para que al montar encuentre el dato ya puesto.
 *
 * 🚨 Y NO ESTABA FUNCIONANDO. La vista inicial estaba COPIADA acá con el vocabulario viejo
 *    (`['sale','fulfilled','closed']`) mientras la pantalla pedía `['open','closed']` desde
 *    ADR-0029 F4. Claves distintas: se calentaba una casilla que nadie leía, el usuario pagaba la
 *    consulta dos veces y la pantalla igual esperaba la suya. Nada fallaba; simplemente no servía.
 *
 * ⭐ Ahora la vista inicial se DERIVA (`vistaInicialDeOps`) en vez de copiarse, y una prueba compara
 *    la clave de la precarga contra la de la pantalla.
 *
 * El porqué de `ensureQueryData` (y no `fetchQuery`), y de no esperar la promesa, está explicado
 * una sola vez en `./productos.ts`.
 */

import { opcionesPedidosOps } from '@/pages/operaciones/opsOrdersQuery';
import { opcionesKpisOps } from '@/pages/operaciones/opsKpisQuery';
import { COLAS, TODAS_LAS_COLAS, vistaInicialDeCola, type ColaDeOperaciones } from '@/lib/operations/colasDeOperaciones';
import { queryClient } from '@/lib/queryClient';
import { marcarHito } from '@/lib/analytics/hitosDelArranque';
import { tenantDelPanel } from './tenantDelPanel';

/**
 * Arranca la carga de una cola. NO espera: React Router espera el valor que devuelve el loader, así
 * que devolver la promesa congelaría la navegación en la pantalla anterior.
 */
export function precargarCola(variante: string) {
  const tenantId = tenantDelPanel();
  if (!tenantId) return null;

  /*
   * El codigo primero: es lo que mas tarda en estar listo y no depende de nada. Sin esperarlo —
   * `Suspense` ya sabe esperar por su cuenta— pero pedido YA, para que viaje junto a los datos.
   */
  /*
   * El codigo primero: es lo que mas tarda en estar listo y no depende de nada.
   *
   * 🚨 LO ENCONTRO LA MEDICION. Con el hito `montado`: los datos llegaban a los 730 ms y el
   *    codigo de la pantalla recien empezaba a los 1.181. React dibujaba la tabla en 200 ms — el
   *    render nunca fue el problema. `FeatureProtectedRoute` tapa la pantalla mientras carga los
   *    permisos, asi que el `import()` ni arrancaba: codigo y datos, que son independientes, se
   *    pedian en serie.
   *
   * ⭐ El `loader` corre ANTES de renderizar, asi que desde aca viajan juntos. Es lo que hacen
   *    React Router 6.4+ (`route.lazy`) y Remix de fabrica.
   */
  /*
   * 🪤 Y SE MARCA CUANDO LLEGA, igual que en `rutaDelPanel`. La primera version de este hito solo
   *    vivia en esa fabrica, asi que las colas —que usan `rutaDeCola`— no lo reportaban: el
   *    instrumento tenia un agujero justo en las pantallas mas medidas.
   */
  void COLAS[variante as ColaDeOperaciones]?.pagina().then(() => marcarHito('chunk')).catch(() => {});

  const inicial = vistaInicialDeCola(variante);

  void queryClient
    .ensureQueryData(opcionesPedidosOps({ tenantId, ...inicial } as Parameters<typeof opcionesPedidosOps>[0]))
    .catch(() => {});

  /*
   * ⭐ LOS KPIs TAMBIEN SE PRECARGAN. Viajan en paralelo con las filas, no despues: son dos
   *    consultas independientes y encadenarlas sumaria sus tiempos por nada. El founder los vio
   *    llegar tarde antes de que la medicion lo dijera.
   */
  void queryClient
    .ensureQueryData(opcionesKpisOps(tenantId, variante, inicial.stages))
    .catch(() => {});

  return null;
}

/**
 * Las colas que comparten la pantalla de Operaciones.
 *
 * 🪤 SE DERIVA, NO SE ESCRIBE. Antes era un arreglo a mano acá y otro en el mapa de paginas de
 *    arriba, mas la lista del menu: tres enumeraciones de las mismas cinco colas. Ahora sale de
 *    la declaracion, asi que una cola nueva entra sola en la precarga, el menu y las pruebas.
 */
export const COLAS_DE_OPERACIONES = TODAS_LAS_COLAS;
export const cargarOpsVentas = () => precargarCola('ventas');
export const cargarOpsPedidos = () => precargarCola('pedidos');
export const cargarOpsCobranzas = () => precargarCola('cobranzas');
export const cargarOpsLogistica = () => precargarCola('logistica');
export const cargarOpsCanceladas = () => precargarCola('canceladas');
